import React, { useState } from 'react';
import { Icon } from 'Components';
import Button from '../Button';
import classNames from 'classnames';
import styles from './AssignmentActionFlowModal.module.scss';
import Modal from '../Modal/Modal';
import AssignmentUsersPicker from './AssignmentUsersPicker';

export type ActionTarget = 'all' | 'specific';

type ModalStep = 'select' | 'confirm' | 'specific' | 'confirm-specific';

export interface ActionTargetOption {
	value: ActionTarget;
	title: string;
	description: string;
}

export interface ConfirmStepCopy {
	title: string;
	intro: string;
	items: string[];
	actionLabel: string;
}

export interface SpecificStepCopy {
	title: string;
	description: string;
	actionLabel: string;
}

export interface SelectStepCopy {
	title: string;
	description: string;
	options: ActionTargetOption[];
	actionLabel: string;
}

interface AssignmentActionFlowModalProps {
	visible: boolean;
	assignmentId: number;
	scopes: string[];
	onCancel: () => void;
	onContinue: (target: ActionTarget, userIds?: string[]) => void;
	select: SelectStepCopy;
	confirmAll: ConfirmStepCopy;
	specific: SpecificStepCopy;
	confirmSpecific: ConfirmStepCopy;
}

interface ConfirmationViewProps {
	title: string;
	intro: string;
	items: string[];
}

const ConfirmationView: React.FC<ConfirmationViewProps> = ({
	title,
	intro,
	items
}) => (
	<div className={styles.body}>
		<div className={styles.title}>{title}</div>

		<div className={styles.confirmContent}>
			<p className={styles.confirmText}>{intro}</p>
			<ul className={styles.confirmList}>
				{items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</div>

		<div className={styles.warning}>
			<Icon type="warningFilledIcon" role="img" />
			This action is permanent and cannot be undone
		</div>
	</div>
);

const AssignmentActionFlowModal: React.FC<AssignmentActionFlowModalProps> = ({
	visible,
	assignmentId,
	scopes,
	onCancel,
	onContinue,
	select,
	confirmAll,
	specific,
	confirmSpecific
}) => {
	const [selected, setSelected] = useState<ActionTarget>('all');
	const [step, setStep] = useState<ModalStep>('select');
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

	const resetState = () => {
		setSelected('all');
		setStep('select');
		setSelectedUserIds([]);
	};

	const handleCancel = () => {
		resetState();
		onCancel();
	};

	const toggleUser = (userId: string) => {
		setSelectedUserIds((prev) =>
			prev.includes(userId)
				? prev.filter((id) => id !== userId)
				: [...prev, userId]
		);
	};

	const handleAction = () => {
		switch (step) {
			case 'select':
				setStep(selected === 'all' ? 'confirm' : 'specific');
				break;
			case 'confirm':
				resetState();
				onContinue('all');
				break;
			case 'specific':
				setStep('confirm-specific');
				break;
			case 'confirm-specific': {
				const userIds = selectedUserIds;
				resetState();
				onContinue('specific', userIds);
				break;
			}
		}
	};

	const actionLabel =
		step === 'select'
			? select.actionLabel
			: step === 'confirm'
			? confirmAll.actionLabel
			: step === 'specific'
			? specific.actionLabel
			: confirmSpecific.actionLabel;

	const isActionDisabled =
		step === 'specific' && selectedUserIds.length === 0;

	const renderBody = () => {
		switch (step) {
			case 'select':
				return (
					<div className={styles.body}>
						<div className={styles.title}>{select.title}</div>
						<p className={styles.description}>{select.description}</p>

						<div className={styles.options}>
							{select.options.map((option) => (
								<label
									key={option.value}
									className={classNames(styles.option, {
										[styles['option--selected']]:
											selected === option.value
									})}
									onClick={() => setSelected(option.value)}
								>
									<input
										type="radio"
										name="action-target"
										value={option.value}
										checked={selected === option.value}
										onChange={() =>
											setSelected(option.value)
										}
										className={styles.radioInput}
									/>
									<div className={styles.optionText}>
										<div className={styles.optionTitle}>
											{option.title}
										</div>
										<div className={styles.optionDesc}>
											{option.description}
										</div>
									</div>
								</label>
							))}
						</div>
					</div>
				);

			case 'confirm':
				return (
					<ConfirmationView
						title={confirmAll.title}
						intro={confirmAll.intro}
						items={confirmAll.items}
					/>
				);

			case 'confirm-specific':
				return (
					<ConfirmationView
						title={confirmSpecific.title}
						intro={confirmSpecific.intro}
						items={confirmSpecific.items}
					/>
				);

			case 'specific':
				return (
					<div className={styles.body}>
						<div className={styles.title}>{specific.title}</div>
						<p className={styles.specificSubtitle}>
							{specific.description}
						</p>

						<AssignmentUsersPicker
							assignmentId={assignmentId}
							scopes={scopes}
							selectedUserIds={selectedUserIds}
							onToggle={toggleUser}
						/>
					</div>
				);
		}
	};

	return (
		<Modal
			visible={visible}
			onCancel={handleCancel}
			className={styles.root}
			centered
			size={step === 'specific' ? 'lg' : 'xs'}
			footer={
				<div className={styles.footer}>
					<Button
						variant="borderless"
						size="small"
						onClick={handleCancel}
					>
						Cancel
					</Button>
					<Button
						size="small"
						type="primary"
						onClick={handleAction}
						disabled={isActionDisabled}
					>
						{actionLabel}
					</Button>
				</div>
			}
		>
			{renderBody()}
		</Modal>
	);
};

export default AssignmentActionFlowModal;
